/**
 * Thin fetch wrapper: authorization, header timeouts, error mapping.
 * The timeout covers time-to-headers only, so long-lived streaming bodies
 * are never cut off; job cancellation aborts through the passed signal.
 */

import { config, resolveAuthToken } from '../core/config';
import { AuthError, apiErrorFromResponse } from '../core/errors';

/**
 * Per-request auth policy (mirrors frontend ``BackendService.add_endpoint``).
 * - ``required``: send Bearer; throw ``AuthError`` if no token is configured
 * - ``optional``: send Bearer only when a token is available
 * - ``none``: never attach credentials
 */
export type AuthRequirement = 'required' | 'optional' | 'none';

export interface HttpRequestOptions {
  method?: string;
  query?: Record<string, any>;
  body?: BodyInit | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Defaults to ``optional`` so inference clients keep working without login. */
  auth?: AuthRequirement;
}

export class HttpClient {
  constructor(private readonly tokenOverride?: string) {}

  private resolveToken(auth: AuthRequirement): string | undefined {
    if (auth === 'none') return undefined;
    const token = this.tokenOverride ?? resolveAuthToken();
    if (auth === 'required' && !token) {
      throw new AuthError('Not authenticated. Set an API key or access token first.');
    }
    return token;
  }

  private authHeaders(auth: AuthRequirement): Record<string, string> {
    const token = this.resolveToken(auth);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Send a request and return the raw (possibly streaming) Response.
   * Throws AuthError when auth is required and missing; ApiError on non-2xx.
   */
  async request(url: string, options: HttpRequestOptions = {}): Promise<Response> {
    const auth = options.auth ?? 'optional';
    const target = appendQuery(url, options.query);
    const timeoutMs = options.timeoutMs ?? config.requestTimeoutMs;

    const timeoutController = new AbortController();
    const timer = setTimeout(() => timeoutController.abort(new Error('Request timed out')), timeoutMs);
    const signal = options.signal
      ? AbortSignal.any([options.signal, timeoutController.signal])
      : timeoutController.signal;

    let response: Response;
    try {
      response = await fetch(target, {
        method: options.method ?? 'GET',
        headers: { ...this.authHeaders(auth), ...options.headers },
        body: options.body ?? null,
        signal,
      });
    } finally {
      // Headers arrived (or the request failed): body streaming must not be time-limited.
      clearTimeout(timer);
    }

    if (!response.ok) throw await apiErrorFromResponse(response);
    return response;
  }
}

function appendQuery(url: string, query?: Record<string, any>): string {
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue;
        params.append(key, String(item));
      }
      continue;
    }
    params.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
  }
  const queryString = params.toString();
  if (!queryString) return url;
  return url.includes('?') ? `${url}&${queryString}` : `${url}?${queryString}`;
}
